import { TrashIcon, DocumentIcon, PhotoIcon } from "@heroicons/react/24/solid";
import {
  CloudArrowUpIcon,
  XMarkIcon,
  EyeIcon,
  InformationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContext } from "context/appContext";
import { ServiceFormProps } from "data/reduxInterfaces";
import { FC, Fragment, HTMLAttributes, useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";

const MAX_FILE_SIZE = 1024 * 1024 * 50;
const ACCEPTED_TARIF_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];
const ACCEPTED_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const formSchema = z.object({
  serviceAgentId: z.number(),
  tarif: z
    .instanceof(File, {
      message: "Document de tarif est obligatoire.",
    })
    .optional()
    .refine((file: File | undefined) => {
      return !file || ACCEPTED_TARIF_MIME_TYPES.includes(file.type);
    }, "Le fichier doit être un PDF ou une image (JPEG, PNG, GIF, WebP) et ne pas dépasser 50MB."),
  images: z
    .array(
      z.instanceof(File, {
        message: "L'image est obligatoire.",
      })
    )
    .refine((files) => {
      return (
        !files ||
        files.every(
          (file) => !file || (file?.size <= MAX_FILE_SIZE && ACCEPTED_IMAGE_MIME_TYPES.includes(file?.type))
        )
      );
    }, "Chaque fichier doit être une image (JPEG, PNG, WebP, GIF) et ne pas dépasser 50MB."),
});

const ServiceFilesUpload: FC<ServiceFormProps & HTMLAttributes<HTMLDivElement>> = ({
  className,
  isLoading,
  handleForm,
  errorServiceForm,
  FormData,
}) => {
  const authContext = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serviceAgentId: authContext.user?.profil?.id ?? undefined,
      tarif: undefined,
    },
    mode: "onSubmit",
  });

  const [images, setImages] = useState<File[]>([]);
  const [tarifFile, setTarifFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  useEffect(() => {
    if (handleForm.type === "chekServiceFormThree") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm.type]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    FormData(data);
  };

  const handleDeleteNewImage = (index: number) => {
    setImages((prevImages) => {
      const updatedNewImages = prevImages.filter((_, i) => i !== index);
      setValue("images", updatedNewImages);
      trigger("images");
      return updatedNewImages;
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files) {
      const fileArray = Array.from(files).filter((file) => ACCEPTED_IMAGE_MIME_TYPES.includes(file.type));
      if (fileArray.length > 0) {
        const newImages = [...images, ...fileArray];
        setImages(newImages);
        setValue("images", newImages);
        trigger("images");
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const newImages = [...images, ...fileArray];
      setImages(newImages);
      setValue("images", newImages);
      trigger("images");
    }
  };

  const handleTarifUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setTarifFile(file);
      setValue("tarif", file);
      trigger("tarif");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type === "application/pdf") {
      return <DocumentIcon className="w-8 h-8 text-red-500" />;
    }
    return <PhotoIcon className="w-8 h-8 text-blue-500" />;
  };

  return (
    <Fragment>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        {/* Header moderne avec icône */}
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-3 bg-eventToBee/10 rounded-xl">
            <PhotoIcon className="w-6 h-6 text-eventToBee" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Documents & Images</h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Ajoutez vos images de présentation et votre grille tarifaire
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Section Images */}
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <PhotoIcon className="w-5 h-5 text-eventToBee" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Images de présentation</h3>
              <span className="text-sm text-gray-500">(optionnel)</span>
            </div>

            {/* Galerie d'images existantes */}
            <AnimatePresence>
              {images.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6"
                >
                  {images.map((image, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="relative group"
                    >
                      <div className="relative aspect-square rounded-xl overflow-hidden">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                            <button
                              type="button"
                              onClick={() => setSelectedImageIndex(index)}
                              className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                            >
                              <EyeIcon className="w-4 h-4 text-gray-700" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteNewImage(index)}
                              className="p-2 bg-red-500 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                            >
                              <TrashIcon className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center truncate">{image.name}</p>
                      <p className="text-xs text-gray-400 text-center">{formatFileSize(image.size)}</p>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Zone de drop moderne */}
            <motion.div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`
                relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300
                ${
                  dragActive
                    ? "border-eventToBee bg-eventToBee/5 scale-105"
                    : "border-gray-300 dark:border-gray-600 hover:border-eventToBee/50"
                }
              `}
              whileHover={{ scale: 1.01 }}
            >
              <input
                id="imageInput"
                name="images"
                type="file"
                multiple
                className="sr-only"
                onChange={handleFileInput}
                accept="image/jpeg, image/jpg, image/png, image/webp, image/gif"
              />

              <motion.div animate={dragActive ? { scale: 1.1 } : { scale: 1 }} className="space-y-4">
                <div
                  className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
                    dragActive ? "bg-eventToBee text-white" : "bg-gray-100 text-gray-400"
                  }`}
                >
                  <CloudArrowUpIcon className="w-8 h-8" />
                </div>

                <div>
                  <label htmlFor="imageInput" className="cursor-pointer">
                    <span
                      className={`text-lg font-medium ${
                        dragActive ? "text-eventToBee" : "text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {dragActive ? "Déposez vos images ici" : "Ajoutez vos images"}
                    </span>
                  </label>
                  <p className="text-sm text-gray-500 mt-1">ou glissez-déposez vos fichiers</p>
                </div>

                <div className="text-xs text-gray-400">
                  <p>Formats supportés: JPEG, PNG, WebP, GIF</p>
                  <p>Taille maximum: 50MB par fichier</p>
                </div>
              </motion.div>
            </motion.div>

            {errors.images && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 rounded-xl p-4"
              >
                <div className="flex items-center space-x-3">
                  <InformationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-800 text-sm font-medium">{errors.images.message}</p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Section Tarifs */}
          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <DocumentIcon className="w-5 h-5 text-eventToBee" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Grille tarifaire</h3>
              <span className="text-sm text-gray-500">(optionnel)</span>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ajoutez votre grille de tarifs (PDF recommandé) pour informer vos clients de vos prix
            </p>

            {/* Fichier tarif existant */}
            {tarifFile && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border border-green-200 rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getFileIcon(tarifFile)}
                    <div>
                      <p className="font-medium text-gray-900">{tarifFile.name}</p>
                      <p className="text-sm text-gray-600">{formatFileSize(tarifFile.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTarifFile(null);
                      setValue("tarif", undefined);
                    }}
                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Zone d'upload de tarif */}
            <div className="border-2 border-gray-200 dark:border-gray-600 border-dashed rounded-xl p-6 text-center hover:border-eventToBee/50 transition-colors">
              <input
                id="tarifInput"
                type="file"
                className="sr-only"
                onChange={handleTarifUpload}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              />

              <div className="space-y-3">
                <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <DocumentIcon className="w-6 h-6 text-gray-400" />
                </div>

                <div>
                  <label
                    htmlFor="tarifInput"
                    className="cursor-pointer text-eventToBee font-medium hover:text-eventToBee/80"
                  >
                    Choisir un fichier
                  </label>
                  <p className="text-sm text-gray-500 mt-1">PDF, JPEG, PNG • Maximum 50MB</p>
                </div>
              </div>
            </div>

            {errors.tarif && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 rounded-xl p-4"
              >
                <div className="flex items-center space-x-3">
                  <InformationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-800 text-sm font-medium">{errors.tarif.message}</p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Conseils d'aide */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-4"
          >
            <div className="flex items-start space-x-3">
              <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Conseils pour vos fichiers</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• Ajoutez des images de qualité montrant vos réalisations</li>
                  <li>• Votre grille tarifaire aide les clients à mieux évaluer vos services</li>
                  <li>• Les fichiers PDF sont recommandés pour les documents officiels</li>
                  <li>• Vous pourrez modifier ces fichiers après la création</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </form>

        {/* Modal de prévisualisation d'image */}
        <AnimatePresence>
          {selectedImageIndex !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImageIndex(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={URL.createObjectURL(images[selectedImageIndex])}
                  alt={`Preview ${selectedImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
                <button
                  onClick={() => setSelectedImageIndex(null)}
                  className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Fragment>
  );
};

export default ServiceFilesUpload;
